import React, { useState } from "react";
import {
	IProfileRequest,
	IProfileResponse,
	IProfileFormElements
} from "../../../../interfaces/user";
import { InjectedFormikProps, withFormik } from "formik";
import { Form, Col, Row, Spinner } from "reactstrap";
import Button from "../../../../components/common/button";
import { ProfileSchema } from "../../../../validations/profileValidation";
import ProfileFormElements from "./profileFormElements";
import userService from "../../../../services/userService";
import { connect } from "react-redux";
import { IApplicationState } from "../../../../redux/reducers";
import ImageUploading, { ImageListType } from "react-images-uploading";
import { Image } from "react-feather";
import TwoFactorSetup from "../twoFactor/twoFactorSetup";

interface IProfileFormProps {
	userId: any;
	formData: IProfileResponse;
	saveProfile: (
		userId: string,
		request: IProfileRequest,
		resolve?: Function,
		reject?: Function
	) => Promise<IProfileResponse>;
	updateProfileImage: (file: File) => Promise<any>;
	updateCompanyLogo: (file: File) => Promise<any>;
}

const ProfileForm: React.FC<InjectedFormikProps<IProfileFormProps, IProfileFormElements>> = (
	props
) => {
	const [uploadingProfileImage, setUploadingProfileImage] = useState<boolean>(false);
	const [uploadingCompanyLogo, setUploadingCompanyLogo] = useState<boolean>(false);

	async function updateProfileImage([{ file }]: ImageListType) {
		if (!file) return;
		setUploadingProfileImage(true);
		try {
			await props.updateProfileImage(file);
		} catch (error) {
		} finally {
			setUploadingProfileImage(false);
		}
	}

	async function updateCompanyLogo([{ file }]: ImageListType) {
		if (!file) {
			return;
		}
		setUploadingCompanyLogo(true);

		try {
			await props.updateCompanyLogo(file);
		} catch (error) {
		} finally {
			setUploadingCompanyLogo(false);
		}
	}

	return (
		<Form onSubmit={props.handleSubmit} className="profile-settings-form">
			<div className="form-content-grid">
				<div className="form-fields-section">
					<ProfileFormElements {...props} />
				</div>
				<div className="upload-section">
					<div className="upload-item">
						<ImageUploading onChange={updateProfileImage}>
							{({ errors, imageList, onImageUpload }) => (
								<div className="image-upload-wrapper">
									<p className="upload-label">User Photo</p>
									<div className="upload-area" onClick={onImageUpload}>
										<div>
											{imageList.length ? (
												<div className="image-upload square">
													{uploadingProfileImage && <Spinner type="grow" color="primary" />}
													<img src={imageList[0].dataURL} alt="Profile preview" />
												</div>
											) : props.formData.user_photo !== "" ? (
												<div className="image-upload square">
													{uploadingProfileImage && <Spinner type="grow" color="primary" />}
													<img src={props.formData.user_photo} loading="lazy" alt="Profile photo" />
												</div>
											) : (
												<div className="upload-placeholder">
													<div className="image-upload square">
														<Image />
													</div>
													<div className="upload-text">
														<span className="upload-text-primary">Choose File</span>
														<span className="upload-text-secondary">no file</span>
													</div>
												</div>
											)}
										</div>
									</div>
								</div>
							)}
						</ImageUploading>
					</div>
					<div className="upload-item">
						<ImageUploading onChange={updateCompanyLogo}>
							{({ imageList, onImageUpload }) => (
								<div className="image-upload-wrapper">
									<p className="upload-label">Company Logo</p>
									<div className="upload-area" onClick={onImageUpload}>
										<div>
											{imageList.length ? (
												<div className="image-upload square">
													{uploadingCompanyLogo && <Spinner type="grow" color="primary" />}
													<img src={imageList[0].dataURL} alt="Company logo preview" />
												</div>
											) : props.formData.company.logo !== "" ? (
												<div className="image-upload square">
													{uploadingCompanyLogo && <Spinner type="grow" color="primary" />}
													<img src={props.formData.company.logo} loading="lazy" alt="Company logo" />
												</div>
											) : (
												<div className="upload-placeholder">
													<div className="image-upload square">
														<Image />
													</div>
													<div className="upload-text">
														<span className="upload-text-primary">Choose File</span>
														<span className="upload-text-secondary">no file</span>
													</div>
												</div>
											)}
										</div>
									</div>
								</div>
							)}
						</ImageUploading>
					</div>
					<div className="upload-item">
						<div className="two-factor-section">
							<TwoFactorSetup phoneNumber={props.formData.phone_number} />
						</div>
					</div>
				</div>
			</div>
			<hr className="form-divider" />
			<Button
				className="btn btn-primary color-white ls-5 font-weight-semibold mt-3 save-button"
				type="submit"
				loading={props.isSubmitting}
				disabled={!props.isValid || props.isSubmitting}
			>
				Save account settings
			</Button>
		</Form>
	);
};

const WrappedForm = withFormik<IProfileFormProps, IProfileFormElements>({
	enableReinitialize: true,
	validationSchema: ProfileSchema,

	mapPropsToValues: (props) => {
		return {
			first_name: props.formData.first_name,
			last_name: props.formData.last_name,
			country: props.formData.country,
			city: props.formData.city,
			company_id: props.formData.company.id,
			company_goal: props.formData.company.company_goal,
			company_objective: props.formData.company.company_objective,
			company_name: props.formData.company.name,
			email: props.formData.email,
			state: props.formData.state,
			user_photo: props.formData.user_photo,
			phone_number: props.formData.phone_number
		};
	},
	handleSubmit: async (
		{ email, company_id, company_name, company_objective, company_goal, ...rest },
		{ props: { saveProfile, userId } }
	) => {
		const updateProfileRequest: IProfileRequest = {
			company: {
				name: company_name,
				id: company_id,
				company_goal,
				company_objective
			},
			...rest
		};
		await saveProfile(userId, updateProfileRequest);
	}
});

const mapStateToProps = ({ user: { user_id } }: IApplicationState) => ({
	userId: user_id
});

const mapDispatchToProps = (dispatch: any) => ({
	saveProfile: (
		userId: string,
		profileData: IProfileRequest,
		resolve?: Function,
		reject?: Function
	) => dispatch(userService.updateProfile(userId, profileData)),
	updateProfileImage: (file: File) => dispatch(userService.updateProfileImage(file)),
	updateCompanyLogo: (file: File) => dispatch(userService.updateCompanyLogo(file))
});

export default connect(mapStateToProps, mapDispatchToProps)(WrappedForm(ProfileForm));
